import {tx, ptx, usePageText} from '../lib/page-text';
import { ReplayCaptionEditor } from "./program/ReplayCaptions";
import { ReplayPublication } from "./program/PublicReplay";
import { useState, useRef, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "../lib/localized-router";
import { api } from "../lib/api-client";
import { useAuth } from "../lib/auth-store";
import { PlayerModal } from "./PlayerModal";
import { PlayIcon } from "./icons";

type Recording = {
  id: string;
  streamId?: string;
  stream?: { id: string; name: string };
  createdAt: string;
  durationMs: number;
  sizeBytes: string | number;
  status: string;
  format: string;
};
type Transfer = {
  recordingId: string;
  phase: string;
  status: string;
  canRetry: boolean;
  message?: string;
};
const phaseLabel: Record<string, string> = {
  queued: "Queued for transfer",
  collecting: "Collecting recording",
  uploading: "Saving replay",
  processing: "Processing",
  ready: "Ready to watch",
  failed: "Recording failed",
  deleted: "Deleted",
};
export function ReplayLibrary({
  recordings,
  streamId,
  onCut,
  footer,
}: {
  recordings: Recording[];
  streamId?: string;
  onCut?: (recording: Recording) => void;
  footer?: (recording: Recording) => ReactNode;
}) {
  usePageText();
  const trigger = useRef<HTMLElement | null>(null);
  const qc = useQueryClient(),
    canEdit = useAuth((s) =>
      ["OWNER", "ADMIN", "MEMBER"].includes(s.user?.role ?? ""),
    );
  const [playing, setPlaying] = useState<{
    id: string;
    url: string;
    format: "mp4" | "hls";
    title: string;
    revision: number;
  } | null>(null);
  const [notice, setNotice] = useState<{ id: string; text: string } | null>(
    null,
  );
  const status = useQuery({
    queryKey: ["replay-transfers", streamId ?? "all"],
    queryFn: async () =>
      (
        await api.get<Transfer[]>(
          streamId
            ? `/me/streams/${streamId}/replays/status`
            : "/me/recordings/transfer-status",
        )
      ).data,
    enabled: recordings.length > 0,
    refetchInterval: recordings.some((r) => r.status === "PROCESSING")
      ? 10000
      : false,
  });
  const play = useMutation({
    mutationFn: async (r: Recording) => {
      const { data } = await api.get(`/me/recordings/${r.id}/playback-url`);
      if (!data.ready)
        throw Error(data.message ?? "This replay is not ready to play yet.");
      return {
        id: r.id,
        url: data.url,
        format: data.format as "hls" | "mp4",
        title: r.stream?.name ?? "Stream replay",
      };
    },
    onSuccess: (data) => {
      setNotice(null);
      setPlaying({ ...data, revision: Date.now() });
    },
    onError: (e: any, r) =>
      setNotice({
        id: r.id,
        text:
          e?.response?.data?.message ??
          e.message ??
          "Could not open this replay. Try again.",
      }),
  });
  const retry = useMutation({
    mutationFn: async (r: Recording) =>
      api.post(
        `/me/streams/${streamId ?? r.streamId ?? r.stream?.id}/replays/${r.id}/retry`,
      ),
    onSuccess: () => {
      setNotice(null);
      void qc.invalidateQueries({ queryKey: ["replay-transfers"] });
      void qc.invalidateQueries({ queryKey: ["recordings"] });
      void qc.invalidateQueries({ queryKey: ["stream"] });
    },
    onError: (e: any, r) =>
      setNotice({
        id: r.id,
        text: e?.response?.data?.message ?? "Could not retry this transfer.",
      }),
  });
  return (
    <>
      {status.isError && (
        <div className="board-notice" role="alert">{tx("Transfer details could not refresh.")}{" "}
          <button
            className="btn outline sm"
            onClick={() => void status.refetch()}
          >{tx("Retry status")}</button>
        </div>
      )}
      <div className="replay-library">
        {recordings.map((r) => {
          const transfer = status.data?.find((x) => x.recordingId === r.id),
            state = transfer?.status ?? r.status,
            phase = transfer?.phase ?? state.toLowerCase();
          return (
            <article className="card replay-card" key={r.id}>
              <div className="replay-card-heading">
                <div>
                  <strong>
                    {r.stream ? (
                      <Link to={`/streams/${r.stream.id}?tab=replays`}>
                        {r.stream.name}
                      </Link>
                    ) : (
                      tx("Broadcast replay")
                    )}
                  </strong>
                  <p className="muted">
                    {new Date(r.createdAt).toLocaleString()}
                  </p>
                </div>
                <span
                  className={`badge ${state === "READY" ? "success" : state === "FAILED" ? "danger" : ""}`}
                  role="status"
                >
                  {tx(phaseLabel[phase] ?? phaseLabel[state.toLowerCase()] ?? state)}
                </span>
              </div>
              <div className="replay-meta">
                <span>
                  {r.durationMs > 0
                    ? `${Math.floor(r.durationMs / 60000)}:${String(Math.floor(r.durationMs / 1000) % 60).padStart(2, "0")}`
                    : tx("Duration pending")}
                </span>
                <span>
                  {Number(r.sizeBytes) > 0
                    ? ptx("{v0} MB", {v0: ((Number(r.sizeBytes) / 1048576).toFixed(1))})
                    : tx("Size pending")}
                </span>
                <span>{r.format.toUpperCase()}</span>
              </div>
              {state === "PROCESSING" && (
                <p className="muted">{tx("Your broadcast has finished. The replay appears here when its file is verified and saved.")}</p>
              )}
              {state === "FAILED" && (
                <p className="muted">
                  {transfer?.message ?? tx("The recording could not be completed.")}{" "}
                  {transfer && !transfer.canRetry
                    ? tx("Its original file cannot be recovered from this page.")
                    : ""}
                </p>
              )}
              {notice?.id === r.id && (
                <p role="alert" className="error">
                  {notice.text}
                </p>
              )}
              <div className="replay-actions">
                {state === "READY" && (
                  <button
                    className="btn primary sm"
                    disabled={play.isPending}
                    onClick={(event) => {
                      trigger.current = event.currentTarget;
                      event.currentTarget.focus();
                      play.mutate(r);
                    }}
                  >
                    <PlayIcon />
                    {play.isPending && play.variables?.id === r.id
                      ? tx("Opening…")
                      : tx("Watch replay")}
                  </button>
                )}
                {transfer?.canRetry && canEdit && (
                  <button
                    className="btn outline sm"
                    disabled={retry.isPending}
                    onClick={() => retry.mutate(r)}
                  >
                    {retry.isPending && retry.variables?.id === r.id
                      ? tx("Retrying…")
                      : tx("Retry transfer")}
                  </button>
                )}
                {state === "READY" && r.durationMs > 0 && onCut && canEdit && (
                  <button className="btn outline sm" onClick={() => onCut(r)}>{tx("Cut clip")}</button>
                )}
                {state === "READY" &&
                  r.format === "mp4" &&
                  (streamId || r.streamId || r.stream?.id) && (
                    <ReplayPublication
                      streamId={(streamId ?? r.streamId ?? r.stream?.id)!}
                      recordingId={r.id}
                    />
                  )}
                {footer?.(r)}
              </div>
              {state === "READY" &&
                (streamId || r.streamId || r.stream?.id) && (
                  <ReplayCaptionEditor
                    streamId={(streamId ?? r.streamId ?? r.stream?.id)!}
                    recordingId={r.id}
                  />
                )}
            </article>
          );
        })}
      </div>
      {playing && (
        <PlayerModal
          recordingId={playing.id}
          streamId={
            streamId ??
            recordings.find((r) => r.id === playing.id)?.streamId ??
            recordings.find((r) => r.id === playing.id)?.stream?.id
          }
          revision={playing.revision}
          restoreFocusTo={trigger.current}
          url={playing.url}
          format={playing.format}
          title={playing.title}
          onClose={() => setPlaying(null)}
          refreshing={play.isPending}
          onRetry={() => {
            const r = recordings.find((r) => r.id === playing.id);
            if (r) play.mutate(r);
          }}
          retryError={notice?.id === playing.id ? notice.text : undefined}
        />
      )}
    </>
  );
}
